import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Minus,
  Plus,
  Truck,
  ShieldCheck,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Star,
  Lightbulb,
  Sliders,
  Ruler,
  Palette,
  Sparkles,
  Mail,
  Bell,
  Share2,
  CheckCircle2,
  Flame,
  PhoneCall,
} from "lucide-react";
import api, { eur, formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import ContactModal from "@/components/ContactModal";
import { toast } from "sonner";

/* ------- helpers ------- */
function SpecRow({ icon: Icon, label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-stone/60 last:border-0">
      <Icon className="h-4 w-4 text-copper mt-0.5 shrink-0" strokeWidth={1.4} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.15em] text-ash">{label}</div>
        <div className="text-sm text-ink mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}

function Tabs({ tabs, active, onChange }) {
  return (
    <div className="border-b border-stone flex flex-wrap gap-1">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          data-testid={`pdp-tab-${t.id}`}
          className={`px-4 py-3 text-xs uppercase tracking-[0.15em] -mb-px border-b-2 transition-colors ${
            active === t.id
              ? "border-copper text-ink"
              : "border-transparent text-ash hover:text-ink"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function RestockForm({ product }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Email requis");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(`/products/${product.slug}/restock-subscribe`, {
        email: email.trim(),
        first_name: firstName.trim() || null,
      });
      setDone(true);
      if (data.already_subscribed) {
        toast.success("Vous êtes déjà inscrit(e) pour ce produit");
      } else {
        toast.success("Inscription enregistrée — nous vous préviendrons.");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="restock-success"
        className="mt-8 border border-copper/40 bg-copper/5 p-6 rounded-sm flex items-start gap-3"
      >
        <CheckCircle2 className="h-5 w-5 text-copper shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-ink">Vous êtes sur la liste.</p>
          <p className="text-sm text-graphite mt-1">
            Nous vous enverrons un email dès que ce luminaire revient en stock — surveillez votre
            boîte de réception (et les spams).
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="restock-form"
      className="mt-8 border border-stone bg-sand p-6 rounded-sm"
    >
      <div className="flex items-start gap-3 mb-4">
        <Flame className="h-5 w-5 text-copper mt-0.5 shrink-0" />
        <div>
          <p className="font-medium text-ink">Ce produit est très demandé.</p>
          <p className="text-sm text-graphite mt-1">
            Laissez votre email — nous vous prévenons en priorité dès qu&apos;il est de nouveau
            disponible. Stock toujours très limité.
          </p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="Prénom (facultatif)"
          data-testid="restock-firstname"
          className="border border-stone bg-cream px-4 py-3 text-sm focus:outline-none focus:border-copper"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Votre email"
          required
          data-testid="restock-email"
          className="border border-stone bg-cream px-4 py-3 text-sm focus:outline-none focus:border-copper"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        data-testid="restock-submit"
        className="w-full bg-ink text-cream py-3 text-xs uppercase tracking-[0.15em] hover:bg-copper transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <Bell className="h-4 w-4" />
        {loading ? "Inscription…" : "Prévenez-moi"}
      </button>
      <p className="text-[10px] text-ash mt-3 text-center">
        Email utilisé uniquement pour cette alerte. Désinscription possible à tout moment.
      </p>
    </form>
  );
}

function shareProduct(product) {
  const url = window.location.href;
  if (navigator.share) {
    navigator
      .share({ title: product.name, text: product.short_description || product.description, url })
      .catch(() => {});
    return;
  }
  navigator.clipboard.writeText(url).then(
    () => toast.success("Lien copié"),
    () => toast.error("Copie impossible")
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, setOpen, paymentEnabled } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [activeIdx, setActiveIdx] = useState(0);
  const [tab, setTab] = useState("description");
  const [contactOpen, setContactOpen] = useState(false);

  useSeo(
    `product:${slug}`,
    product
      ? {
          title: `${product.name} | Déstockage Luminaires`,
          description: product.short_description || product.description,
          og_image: product.image,
        }
      : undefined
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setActiveIdx(0);
    setQty(1);
    setTab("description");
    api
      .get(`/products/${slug}`)
      .then(async ({ data }) => {
        setProduct(data);
        const { data: rel } = await api.get("/products", {
          params: { category: data.category_slug, limit: 6 },
        });
        setRelated(rel.filter((p) => p.slug !== slug).slice(0, 4));
      })
      .catch(() => navigate("/boutique"))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const imgs = (product.images && product.images.length > 0) ? product.images : [product.image];
    return imgs.filter(Boolean);
  }, [product]);

  if (loading || !product) {
    return (
      <div className="px-6 md:px-12 pt-8 grid md:grid-cols-2 gap-10">
        <div className="aspect-[4/5] bg-stone animate-pulse rounded-sm" />
        <div className="space-y-4 pt-6">
          <div className="h-4 w-24 bg-stone animate-pulse" />
          <div className="h-10 w-3/4 bg-stone animate-pulse" />
          <div className="h-6 w-32 bg-stone animate-pulse" />
        </div>
      </div>
    );
  }

  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;
  const outOfStock = product.stock <= 0;
  const lowStock = !outOfStock && product.stock > 0 && product.stock <= 3;

  const handleAdd = () => {
    if (outOfStock) return;
    addItem(product, qty);
    toast.success(`${product.name} ajouté au panier`);
  };
  const buyNow = () => {
    if (outOfStock) return;
    addItem(product, qty);
    setOpen(false);
    navigate("/checkout");
  };

  const contactAboutProduct = () => {
    if (outOfStock) return;
    setContactOpen(true);
  };

  const colorTempLabel = {
    chaud: "Blanc chaud (2700–3000 K)",
    neutre: "Blanc neutre (4000 K)",
    froid: "Blanc froid (5000–6500 K)",
  }[product.color_temp];

  return (
    <div className="px-6 md:px-12 pt-6 md:pt-10">
      <button
        data-testid="back-btn"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-ash hover:text-ink transition-colors mb-6"
      >
        <ChevronLeft className="h-4 w-4" /> Retour
      </button>

      <div className="grid md:grid-cols-2 gap-8 md:gap-16">
        {/* Galerie */}
        <div className="md:sticky md:top-28 h-fit">
          <div className="aspect-[4/5] overflow-hidden bg-sand relative rounded-sm group">
            <img
              key={activeIdx}
              src={gallery[activeIdx]}
              alt={product.name}
              className="w-full h-full object-cover animate-[fadeIn_0.3s_ease]"
              data-testid="pdp-main-image"
            />
            {discount > 0 && (
              <span className="absolute top-4 left-4 bg-gold text-ink text-xs font-mono px-2.5 py-1">
                -{discount}%
              </span>
            )}
            {gallery.length > 1 && (
              <>
                <button
                  onClick={() =>
                    setActiveIdx((i) => (i - 1 + gallery.length) % gallery.length)
                  }
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-cream/90 hover:bg-cream p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Image précédente"
                  data-testid="pdp-prev-img"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setActiveIdx((i) => (i + 1) % gallery.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-cream/90 hover:bg-cream p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Image suivante"
                  data-testid="pdp-next-img"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}
          </div>

          {gallery.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {gallery.slice(0, 5).map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIdx(i)}
                  data-testid={`pdp-thumb-${i}`}
                  className={`aspect-square overflow-hidden bg-sand border-2 transition-colors ${
                    i === activeIdx ? "border-copper" : "border-transparent hover:border-stone"
                  }`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="md:py-2">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-[0.2em] text-gold">{product.category}</p>
            <button
              onClick={() => shareProduct(product)}
              className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-ash hover:text-ink"
              data-testid="pdp-share"
            >
              <Share2 className="h-3.5 w-3.5" />
              Partager
            </button>
          </div>

          <h1
            data-testid="product-title"
            className="font-serif text-3xl md:text-5xl text-ink tracking-tight leading-tight"
          >
            {product.name}
          </h1>

          <div className="flex items-center gap-2 mt-4">
            <div className="flex">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-gold text-gold" strokeWidth={0} />
              ))}
            </div>
            <span className="text-xs text-ash font-mono">
              {product.rating?.toFixed(1)}
              {product.brand ? ` · ${product.brand}` : ""}
              {product.sku ? ` · ${product.sku}` : ""}
            </span>
          </div>

          {/* Prix + économie */}
          <div className="flex flex-wrap items-baseline gap-3 mt-6">
            <span data-testid="product-price" className="font-mono text-3xl text-ink">
              {eur(product.price)}
            </span>
            {product.original_price && (
              <>
                <span className="font-mono text-lg text-ash line-through">
                  {eur(product.original_price)}
                </span>
                <span className="text-xs uppercase tracking-[0.15em] text-copper">
                  Économie {eur(product.original_price - product.price)}
                </span>
              </>
            )}
          </div>

          {/* Short description */}
          {(product.short_description || product.description) && (
            <p className="text-graphite leading-relaxed mt-5 italic">
              {product.short_description || (product.description || "").slice(0, 220)}
            </p>
          )}

          {/* Highlights (chips) */}
          <div className="flex flex-wrap gap-2 mt-5">
            {product.dimmable && (
              <span className="text-[11px] uppercase tracking-[0.1em] border border-copper text-copper px-2.5 py-1">
                Variateur intégré
              </span>
            )}
            {product.lumens && (
              <span className="text-[11px] uppercase tracking-[0.1em] border border-stone text-graphite px-2.5 py-1">
                {product.lumens.toLocaleString("fr-FR")} lumens
              </span>
            )}
            {product.color_temp && (
              <span className="text-[11px] uppercase tracking-[0.1em] border border-stone text-graphite px-2.5 py-1">
                {colorTempLabel}
              </span>
            )}
            {product.ip_rating && (
              <span className="text-[11px] uppercase tracking-[0.1em] border border-stone text-graphite px-2.5 py-1">
                {product.ip_rating}
              </span>
            )}
            {product.brand && (
              <span className="text-[11px] uppercase tracking-[0.1em] border border-stone text-graphite px-2.5 py-1">
                Marque {product.brand}
              </span>
            )}
          </div>

          {/* Stock state */}
          {outOfStock ? (
            <RestockForm product={product} />
          ) : (
            <>
              {lowStock && (
                <div
                  data-testid="low-stock-warning"
                  className="mt-6 flex items-center gap-2 text-sm text-copper"
                >
                  <Flame className="h-4 w-4" />
                  Plus que <strong>{product.stock}</strong> en stock — produit très demandé
                </div>
              )}

              <div className="flex items-center gap-4 mt-7">
                <div className="flex items-center border border-ink">
                  <button
                    data-testid="qty-dec"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="px-3.5 py-3.5 hover:bg-sand"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span data-testid="qty-value" className="px-5 font-mono">
                    {qty}
                  </span>
                  <button
                    data-testid="qty-inc"
                    onClick={() => setQty((q) => Math.min(product.stock, q + 1))}
                    className="px-3.5 py-3.5 hover:bg-sand"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  data-testid="add-to-cart-button"
                  onClick={handleAdd}
                  className="flex-1 border border-ink text-ink py-4 uppercase tracking-[0.15em] text-xs hover:bg-ink hover:text-cream transition-colors"
                >
                  Ajouter au panier
                </button>
                {paymentEnabled ? (
                  <button
                    data-testid="buy-now-button"
                    onClick={buyNow}
                    className="flex-1 bg-ink text-cream py-4 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors"
                  >
                    Commander · {eur(product.price * qty)}
                  </button>
                ) : (
                  <button
                    data-testid="contact-now-button"
                    onClick={contactAboutProduct}
                    className="flex-1 bg-ink text-cream py-4 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors flex items-center justify-center gap-2"
                  >
                    <PhoneCall className="h-4 w-4" /> Contacter
                  </button>
                )}
              </div>
            </>
          )}

          {/* Trust badges */}
          <div className="grid grid-cols-3 gap-4 mt-10 pt-8 border-t border-stone">
            {[
              [Truck, "Livraison offerte", "dès 50€"],
              [RotateCcw, "Retour 15 jours", "remboursé"],
              [ShieldCheck, "Garantie 1 an", "fabrication"],
            ].map(([Icon, t, s], i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2">
                <Icon className="h-5 w-5 text-copper" strokeWidth={1.4} />
                <p className="text-xs font-medium text-ink leading-tight">{t}</p>
                <p className="text-[10px] text-ash">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs section */}
      <section className="mt-16 md:mt-24">
        <Tabs
          tabs={[
            { id: "description", label: "Description" },
            { id: "specs", label: "Caractéristiques" },
            { id: "delivery", label: "Livraison & retours" },
          ]}
          active={tab}
          onChange={setTab}
        />

        <div className="pt-8 max-w-3xl">
          {tab === "description" && (
            <div
              className="text-graphite leading-relaxed whitespace-pre-line"
              data-testid="pdp-desc"
            >
              {product.description || "Aucune description disponible."}
            </div>
          )}

          {tab === "specs" && (
            <div data-testid="pdp-specs" className="grid sm:grid-cols-2 gap-x-10">
              <div>
                <SpecRow icon={Lightbulb} label="Flux lumineux"
                  value={product.lumens ? `${product.lumens.toLocaleString("fr-FR")} lumens` : null} />
                <SpecRow icon={Lightbulb} label="Teinte de lumière" value={colorTempLabel} />
                <SpecRow icon={Sliders} label="Variateur"
                  value={product.dimmable ? "Intégré (intensité variable)" : null} />
                <SpecRow icon={Lightbulb} label="Puissance"
                  value={product.wattage ? `${product.wattage} W` : null} />
                <SpecRow icon={ShieldCheck} label="Indice de protection" value={product.ip_rating} />
              </div>
              <div>
                <SpecRow icon={Ruler} label="Dimensions" value={product.dimensions} />
                <SpecRow icon={Palette} label="Finition" value={product.finish} />
                <SpecRow icon={Sparkles} label="Matière" value={product.material} />
                <SpecRow
                  icon={Palette}
                  label="Coloris disponibles"
                  value={product.colors_available?.length ? product.colors_available.join(", ") : null}
                />
                <SpecRow icon={Mail} label="Référence" value={product.sku} />
              </div>
            </div>
          )}

          {tab === "delivery" && (
            <div className="space-y-4 text-graphite leading-relaxed" data-testid="pdp-delivery">
              <p>
                <strong className="text-ink">Livraison offerte dès 50€</strong> en France
                métropolitaine et en Belgique. En dessous, livraison à partir de 4,90€.
              </p>
              <p>
                Expédition sous 24/48h ouvrées via Colissimo, Mondial Relay ou Chronopost selon
                votre choix au moment du paiement.
              </p>
              <p>
                <strong className="text-ink">Retour gratuit sous 15 jours</strong> — produit
                non utilisé dans son emballage d&apos;origine. Remboursement intégral sous 7
                jours après réception.
              </p>
              <p>
                <strong className="text-ink">Garantie fabricant 1 an</strong> sur tous nos
                luminaires. SAV réactif par email et téléphone.
              </p>
            </div>
          )}
        </div>
      </section>

      {related.length > 0 && (
        <section className="mt-20 md:mt-28">
          <h2 className="font-serif text-3xl text-ink tracking-tight mb-8">
            Vous aimerez aussi
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10 md:gap-x-7">
            {related.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}

      <ContactModal
        open={contactOpen}
        onOpenChange={setContactOpen}
        items={[{ product_id: product.id, slug: product.slug, name: product.name, price: product.price, image: product.image, quantity: qty }]}
        subtotal={product.price * qty}
      />
    </div>
  );
}
